/**
 * Client-side heuristic to suggest the most relevant template for pasted
 * content. Zero-cost, runs on every keystroke (memoize upstream).
 *
 * Approach:
 * 1. Extract lightweight signals from the pasted content (bullets, story
 *    markers, first-person, contrarian words, word count, etc.).
 * 2. Extract keyword tags from every template (name + description +
 *    structure). Each tag maps to a signal weight table.
 * 3. Score each template = sum of (tag weight * signal presence).
 * 4. Return the top template above a minimum threshold. Otherwise null.
 *
 * The design lets the heuristic cover both the seeded presets and any
 * user-created custom templates without hardcoding template IDs.
 */

const MIN_CONTENT_CHARS = 80;
const MIN_SCORE = 2; // below this, no suggestion is shown

// Signal weights per keyword tag. When a template's text contains a
// keyword from a tag, that tag's signal weights apply to the template.
const TAG_SIGNALS = {
  list: { bullets: 3, numbered_list: 3, short_lines: 1 },
  story: { first_person: 2, story_words: 3, past_tense: 2, long_paragraphs: 1 },
  hook: { opinion_words: 2, question: 1, imperative: 1 },
  contrarian: { contrarian_words: 4, opinion_words: 2, question: 1 },
  howto: { imperative: 3, numbered_list: 2, bullets: 1 },
};

// Which keywords in template text activate which tags.
const KEYWORD_TAGS = {
  list: ["list", "listicle", "bullet", "point", "steps", "step-by-step", "tips", "rules", "reasons"],
  story: ["story", "narrative", "arc", "moment", "journey", "personal", "lesson", "anecdote"],
  hook: ["hook", "cta", "call to action", "opening", "opener"],
  contrarian: ["contrarian", "hot take", "myth", "wrong", "unpopular", "opposite", "controversy"],
  howto: ["how-to", "how to", "guide", "framework", "playbook", "tutorial"],
};

const STORY_WORDS = ["yesterday", "when i", "when we", "then", "remember", "once", "back when", "story", "learned"];
const CONTRARIAN_WORDS = [
  "actually", "myth", "wrong", "hot take", "unpopular", "opposite", "contrary",
  "everyone thinks", "conventional wisdom", "let me push back",
];
const OPINION_WORDS = ["i think", "i believe", "in my opinion", "honestly", "here's the truth", "here is the truth"];
const IMPERATIVE_OPENERS = ["do ", "don't ", "stop ", "start ", "avoid ", "try ", "use ", "build ", "ship ", "learn "];

function countMatches(haystack, needles) {
  let n = 0;
  for (const needle of needles) {
    // rough word-boundary-ish match, cheap
    const re = new RegExp(`(^|[^a-z])${needle.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}([^a-z]|$)`, "gi");
    const m = haystack.match(re);
    if (m) n += m.length;
  }
  return n;
}

function extractSignals(rawContent) {
  const content = (rawContent || "").trim();
  if (!content) return null;
  const lower = content.toLowerCase();
  const lines = content.split(/\n+/).filter((l) => l.trim().length > 0);
  const words = content.split(/\s+/).filter(Boolean);

  const bullets = lines.filter((l) => /^\s*[-*•]\s+/.test(l)).length;
  const numberedList = lines.filter((l) => /^\s*\d+[.)]\s+/.test(l)).length;
  const shortLines = lines.filter((l) => l.trim().split(/\s+/).length <= 12).length;
  const longParagraphs = lines.filter((l) => l.trim().split(/\s+/).length >= 30).length;

  const firstPerson =
    countMatches(lower, ["i", "i'm", "i've", "we", "our", "my", "us"]);
  const question = (content.match(/\?/g) || []).length;
  const storyWords = countMatches(lower, STORY_WORDS);
  const contrarianWords = countMatches(lower, CONTRARIAN_WORDS);
  const opinionWords = countMatches(lower, OPINION_WORDS);
  const pastTense = countMatches(lower, ["was", "were", "had", "did", "went", "saw", "said"]);
  const imperative = lines.filter((l) =>
    IMPERATIVE_OPENERS.some((op) => l.trim().toLowerCase().startsWith(op))
  ).length;

  const listRatio = lines.length ? (bullets + numberedList) / lines.length : 0;

  return {
    words: words.length,
    lines: lines.length,
    bullets: bullets >= 2 ? 1 : 0,
    numbered_list: numberedList >= 2 ? 1 : 0,
    short_lines: shortLines >= 3 ? 1 : 0,
    long_paragraphs: longParagraphs >= 1 ? 1 : 0,
    first_person: firstPerson >= 3 ? 1 : 0,
    question: question >= 1 ? 1 : 0,
    story_words: storyWords >= 1 ? 1 : 0,
    contrarian_words: contrarianWords >= 1 ? 1 : 0,
    opinion_words: opinionWords >= 1 ? 1 : 0,
    past_tense: pastTense >= 3 ? 1 : 0,
    imperative: imperative >= 1 ? 1 : 0,
    list_ratio: listRatio,
  };
}

function tagsForTemplate(tpl) {
  const text = [tpl.name || "", tpl.description || "", tpl.structure || ""]
    .join(" ")
    .toLowerCase();
  const tags = new Set();
  for (const [tag, keywords] of Object.entries(KEYWORD_TAGS)) {
    if (keywords.some((k) => text.includes(k))) tags.add(tag);
  }
  return tags;
}

function scoreTemplate(tpl, signals) {
  if (!signals) return 0;
  const tags = tagsForTemplate(tpl);
  if (tags.size === 0) return 0;
  let score = 0;
  for (const tag of tags) {
    const weights = TAG_SIGNALS[tag] || {};
    for (const [signal, weight] of Object.entries(weights)) {
      if (signals[signal]) score += weight;
    }
  }
  return score;
}

/**
 * Pick the highest-scoring template for the given content.
 * Returns { template, score, reason } or null if no confident pick.
 */
export function suggestBestTemplate(content, templates) {
  if (!Array.isArray(templates) || templates.length === 0) return null;
  if (!content || content.trim().length < MIN_CONTENT_CHARS) return null;
  const signals = extractSignals(content);
  if (!signals) return null;

  let best = null;
  for (const tpl of templates) {
    const s = scoreTemplate(tpl, signals);
    if (s > 0 && (!best || s > best.score)) {
      best = { template: tpl, score: s };
    }
  }
  if (!best || best.score < MIN_SCORE) return null;

  // Build a short human-readable reason
  const tags = tagsForTemplate(best.template);
  let reason = "matches your content shape";
  if (tags.has("list") && (signals.bullets || signals.numbered_list)) {
    reason = "your content already reads like a list";
  } else if (tags.has("story") && signals.first_person && signals.story_words) {
    reason = "your content feels like a personal story";
  } else if (tags.has("contrarian") && signals.contrarian_words) {
    reason = "your content pushes back on a common belief";
  } else if (tags.has("howto") && signals.imperative) {
    reason = "your content is step-by-step advice";
  } else if (tags.has("hook") && signals.opinion_words) {
    reason = "your content leads with an opinion";
  }

  return { template: best.template, score: best.score, reason };
}

// Exposed for tests / debugging
export const _internal = { extractSignals, tagsForTemplate, scoreTemplate };
